import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminApi, ADMIN_TOKEN_KEY } from "@/lib/api";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      setAdmin(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await adminApi.get("/admin/auth/me");
      setAdmin(data);
    } catch (e) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await adminApi.post("/admin/auth/login", { email, password });
    if (data.password_setup_required || data.two_factor_required) return data;
    localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
    setAdmin(data.admin);
    return data;
  };

  const completeSetup = async (setupToken, newPassword) => {
    const { data } = await adminApi.post("/admin/auth/set-password", {
      setup_token: setupToken, new_password: newPassword,
    });
    if (data.two_factor_required) return data;
    localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
    setAdmin(data.admin);
    return data.admin;
  };

  const verifyTwoFactor = async (challengeId, code) => {
    const { data } = await adminApi.post("/admin/auth/2fa/verify", { challenge_id: challengeId, code });
    localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
    setAdmin(data.admin);
    return data.admin;
  };

  const resendTwoFactor = async (challengeId) => {
    const { data } = await adminApi.post("/admin/auth/2fa/resend", { challenge_id: challengeId });
    return data;
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, completeSetup, verifyTwoFactor, resendTwoFactor, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
